import React, { useCallback, useEffect, useState } from "react";
import { Plus, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MONTHS_IT } from "@/lib/constants";
import { formatDateTimeIT } from "@/foundation/formatting";
import { CounterAdjustmentDialog, COUNTER_LABELS } from "@/components/employees/CounterAdjustmentDialog";

const period = (y, m) => (y && m ? `${MONTHS_IT[m - 1]} ${y}` : "—");

const Row = ({ r, onReverse }) => (
  <tr className="border-t border-suite-neutral-100" data-testid={`adjustment-row-${r.id}`}>
    <td className="px-3 py-2">{period(r.booking_year, r.booking_month)}</td>
    <td className="px-3 py-2">{period(r.origin_year, r.origin_month)}</td>
    <td className="px-3 py-2">{COUNTER_LABELS[r.counter_type]}</td>
    <td className="px-3 py-2 text-right font-mono">{r.quantity > 0 ? "+" : ""}{Number(r.quantity).toFixed(3)} {r.unit === "days" ? "gg" : "h"}</td>
    <td className="px-3 py-2 max-w-xs truncate" title={r.reason}>{r.reason}</td>
    <td className="px-3 py-2 text-suite-neutral-500">{formatDateTimeIT(r.created_at)}</td>
    <td className="px-3 py-2">
      {r.reverses_id ? <span className="text-suite-neutral-500">Storno</span>
        : r.reversed_by_id ? <span className="text-amber-700">Stornata</span> : "Attiva"}
    </td>
    <td className="px-3 py-2 text-right">
      {!r.reverses_id && !r.reversed_by_id && (
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onReverse(r)} data-testid={`reverse-adjustment-${r.id}`}>
          <Undo2 size={13} className="mr-1" /><span className="text-xs">Storna</span>
        </Button>
      )}
    </td>
  </tr>
);

export function CounterAdjustmentsPanel({ employeeId, onChanged }) {
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState(null);
  const load = useCallback(() => api.get(`/counters-consolidation/employees/${employeeId}/adjustments`)
    .then(({ data }) => setInfo(data)).catch(() => setInfo(null)), [employeeId]);
  useEffect(() => { load(); }, [load]);
  if (!info?.enabled) return null;

  const saved = () => { setMode(null); load(); onChanged?.(); };
  return (
    <div className="rounded-lg border border-suite-neutral-200 bg-white" data-testid="counter-adjustments-panel">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-suite-neutral-100">
        <div>
          <div className="text-sm font-semibold text-suite-neutral-800">Rettifiche Contatori</div>
          <div className="text-xs text-suite-neutral-500" data-testid="adjustments-booking-info">
            {info.first_open_month ? `Primo mese aperto per la registrazione: ${period(info.first_open_month.year, info.first_open_month.month)}` : info.blocked_reason}
          </div>
        </div>
        <Button size="sm" onClick={() => setMode("create")} disabled={!info.first_open_month} data-testid="new-adjustment-btn">
          <Plus size={14} className="mr-1" /> Nuova rettifica
        </Button>
      </div>
      {info.items.length === 0 ? (
        <div className="px-4 py-4 text-xs text-suite-neutral-500" data-testid="adjustments-empty">Nessuna rettifica registrata.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="adjustments-table">
            <thead className="bg-suite-neutral-50 text-suite-neutral-600">
              <tr>{["Registrazione", "Competenza", "Contatore", "Quantità", "Motivazione", "Data", "Stato", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>{info.items.map((r) => <Row key={r.id} r={r} onReverse={(e) => setMode({ reverse: e })} />)}</tbody>
          </table>
        </div>
      )}
      <CounterAdjustmentDialog employeeId={employeeId} info={info} mode={mode} onClose={() => setMode(null)} onSaved={saved} />
    </div>
  );
}
